/**
 * @file LaunchPage.tsx
 * @description Smart TV Launcher and multi-screen pairing console for TableGreet Digital TV Signage Engine.
 * 
 * CORE RESPONSIBILITIES:
 * 1. Automatically requests a unique 6-digit TV pairing code and renders a dynamic QR code on screen.
 * 2. Subscribes to Supabase Realtime (with polling fail-safe) awaiting manager approval from any smartphone.
 * 3. Persists session credentials, screen identity, and venue slug for instant auto-resuming across TV reboots.
 * 4. Supports multiple simultaneous TV screens across branches (Bar TV, Patio TV, Dining TV, etc.).
 * 5. Provides fallback tabs for manual pairing PIN entry and direct venue slug launching.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Tv,
  ArrowRight,
  MonitorPlay,
  QrCode,
  KeyRound,
  RefreshCw,
  CheckCircle2,
  Clock,
  Smartphone,
  ShieldCheck,
  RotateCcw,
} from 'lucide-react';
import logo from '/logo.svg';
import { publicSupabase } from '../lib/supabase';
import { getSignageSessionKey, getSignageScreenIdKey } from '../lib/signage';

export const LaunchPage: React.FC = () => {
  const navigate = useNavigate();

  // Active view: 'pair' (QR code & 6-digit PIN) vs 'manual' (PIN / slug input)
  const [activeTab, setActiveTab] = useState<'pair' | 'manual'>('pair');

  // TV Pairing State
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [pairingError, setPairingError] = useState<string | null>(null);

  // Success Celebration State
  const [isApproved, setIsApproved] = useState(false);
  const [approvedDetails, setApprovedDetails] = useState<{
    screenName: string;
    venueSlug: string;
  } | null>(null);

  // Manual Input State
  const [manualCodeInput, setManualCodeInput] = useState('');
  const [isVerifyingManualCode, setIsVerifyingManualCode] = useState(false);
  const [manualCodeError, setManualCodeError] = useState<string | null>(null);

  const [slugInput, setSlugInput] = useState('');
  const [lastSlug, setLastSlug] = useState<string | null>(null);

  // References to manage timers and subscriptions cleanly
  const realtimeChannelRef = useRef<ReturnType<typeof publicSupabase.channel> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for previously saved display slug or pairing code in URL on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('tablegreet_last_signage_slug');
      if (saved) setLastSlug(saved);

      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get('code') || searchParams.get('pair');
      if (code) {
        setManualCodeInput(code.toUpperCase());
        setActiveTab('manual');
      }
    }
  }, []);

  // Handles successful TV pairing and persists credentials
  const handlePairingSuccess = useCallback(
    (details: {
      venueSlug: string;
      venueId?: string;
      screenId?: string;
      token?: string;
      screenName?: string;
    }) => {
      setIsApproved(true);
      setApprovedDetails({
        screenName: details.screenName || 'TV Display',
        venueSlug: details.venueSlug,
      });

      // Persist credentials across reboots and power outages
      if (typeof window !== 'undefined') {
        localStorage.setItem('tablegreet_last_signage_slug', details.venueSlug);
        if (details.venueId && details.token) {
          const sessionKey = getSignageSessionKey(details.venueId);
          sessionStorage.setItem(sessionKey, details.token);
          localStorage.setItem(sessionKey, details.token);
        }
        if (details.venueId && details.screenId) {
          localStorage.setItem(getSignageScreenIdKey(details.venueId), details.screenId);
        }
        if (details.venueId && details.screenName) {
          localStorage.setItem(`tablegreet_signage_screen_name_${details.venueId}`, details.screenName);
        }
      }

      // Smoothly navigate to the live menu display
      const redirectTimer = setTimeout(() => {
        navigate(`/${details.venueSlug}/signage`);
      }, 1600);

      return () => clearTimeout(redirectTimer);
    },
    [navigate]
  );

  // Request a fresh 6-digit TV pairing session from Supabase
  const requestPairingCode = useCallback(async () => {
    setIsGeneratingCode(true);
    setPairingError(null);

    // Teardown existing channel and intervals before generating new code
    if (realtimeChannelRef.current) {
      void publicSupabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    try {
      const deviceInfo = {
        resolution: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '1920x1080',
        orientation: typeof window !== 'undefined' && window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Smart TV',
      };

      const { data, error } = await publicSupabase.rpc('request_tv_pairing_code', {
        p_device_info: deviceInfo,
      });

      if (error) throw error;
      if (!data?.pairing_code || !data?.session_id) {
        throw new Error('Invalid pairing session response.');
      }

      setSessionId(data.session_id);
      setPairingCode(data.pairing_code);
      const expiry = new Date(data.expires_at);
      setExpiresAt(expiry);

      // 1. Setup Supabase Realtime listener on this specific pairing session
      const channel = publicSupabase
        .channel(`pairing_${data.session_id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'signage_pairing_sessions',
            filter: `id=eq.${data.session_id}`,
          },
          (payload) => {
            const row = payload.new as any;
            if (row?.status === 'approved' && row?.venue_slug) {
              handlePairingSuccess({
                venueSlug: row.venue_slug,
                venueId: row.venue_id,
                screenId: row.screen_id,
                token: row.session_token,
                screenName: row.screen_name,
              });
            }
          }
        )
        .subscribe();

      realtimeChannelRef.current = channel;

      // 2. Setup fail-safe polling every 3 seconds for TVs with restrictive websockets
      const currentSessionId = data.session_id;
      pollIntervalRef.current = setInterval(async () => {
        try {
          const { data: statusData } = await publicSupabase.rpc('get_tv_pairing_status', {
            p_session_id: currentSessionId,
          });

          if (statusData?.status === 'approved' && statusData?.venue_slug) {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            handlePairingSuccess({
              venueSlug: statusData.venue_slug,
              venueId: statusData.venue_id,
              screenId: statusData.screen_id,
              token: statusData.token,
              screenName: statusData.screen_name,
            });
          }
        } catch {
          // Silent polling retry
        }
      }, 3000);
    } catch (err: any) {
      console.error('[LaunchPage] Failed to request pairing code:', err);
      setPairingError('Unable to generate pairing code. Check network connection.');
    } finally {
      setIsGeneratingCode(false);
    }
  }, [handlePairingSuccess]);

  // Request pairing code when entering the 'pair' tab
  useEffect(() => {
    if (activeTab === 'pair' && !pairingCode && !isApproved) {
      void requestPairingCode();
    }

    return () => {
      if (realtimeChannelRef.current) {
        void publicSupabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [activeTab, pairingCode, isApproved, requestPairingCode]);

  // Countdown timer for pairing code expiration
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = Date.now();
      const diffMs = expiresAt.getTime() - now;

      if (diffMs <= 0) {
        setTimeRemaining('Expired');
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        return;
      }

      const totalSec = Math.floor(diffMs / 1000);
      const minutes = Math.floor(totalSec / 60);
      const seconds = totalSec % 60;
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    countdownIntervalRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [expiresAt]);

  // Handle manual code entry (from admin-generated PIN)
  const handleManualCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = manualCodeInput.replace(/-/g, '').trim().toUpperCase();
    if (!clean) return;

    setIsVerifyingManualCode(true);
    setManualCodeError(null);

    try {
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
      const { data, error } = await publicSupabase.rpc('pair_signage_screen', {
        p_code: clean,
        p_user_agent: userAgent,
      });

      if (error) throw error;
      if (data?.venue_slug) {
        handlePairingSuccess({
          venueSlug: data.venue_slug,
          venueId: data.venue_id,
          screenId: data.screen_id,
          token: data.token,
          screenName: data.screen_name || data.name,
        });
      } else {
        setManualCodeError('Invalid or expired code. Please verify in your admin dashboard.');
      }
    } catch (err: any) {
      setManualCodeError(err.message || 'Unable to connect display. Check code and try again.');
    } finally {
      setIsVerifyingManualCode(false);
    }
  };

  // Handle direct venue slug launching
  const handleLaunchSlug = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = slugInput.trim().toLowerCase();
    if (!trimmed) return;
    navigate(`/${trimmed}/signage`);
  };

  // Format 6-digit code with clean spacing (e.g. "849 201")
  const formattedCode = pairingCode
    ? pairingCode.length === 6
      ? `${pairingCode.slice(0, 3)}  ${pairingCode.slice(3)}`
      : pairingCode
    : '--- ---';

  // Construct URL for QR code pointing directly to standalone signage engine pairing
  const qrPairingUrl = pairingCode
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/pair?code=${pairingCode}`
    : '';

  return (
    <div className="min-h-screen bg-[#070707] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden select-none">
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,79,0,0.06)_0%,transparent_50%),linear-gradient(180deg,#0a0a0a_0%,#050505_100%)]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-brand-primary/5 rounded-full blur-[160px] pointer-events-none" />

      {/* Main Container */}
      <div className="relative z-10 max-w-xl w-full mx-auto p-8 sm:p-10 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl shadow-2xl">
        {/* Header Branding */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src={logo} alt="TableGreet" className="w-12 h-12 rounded-xl object-contain shadow-lg" />
          <div className="text-left">
            <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-2">
              TableGreet Signage
            </h1>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand-primary">
              Digital TV Display Engine
            </p>
          </div>
        </div>

        {/* Tab Selection */}
        {!isApproved && (
          <div className="flex items-center justify-center p-1 rounded-2xl bg-white/[0.04] border border-white/10 mb-8 max-w-sm mx-auto">
            <button
              type="button"
              onClick={() => setActiveTab('pair')}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeTab === 'pair'
                  ? 'bg-brand-primary text-black font-black shadow-md'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <QrCode size={14} />
              Pair TV Screen
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('manual')}
              className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeTab === 'manual'
                  ? 'bg-brand-primary text-black font-black shadow-md'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <KeyRound size={14} />
              Manual Entry
            </button>
          </div>
        )}

        {/* Success Celebration Screen */}
        {isApproved ? (
          <div className="py-8 space-y-4 animate-fadeIn">
            <div className="h-20 w-20 mx-auto rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center border border-emerald-500/30 shadow-lg shadow-emerald-500/10">
              <CheckCircle2 size={44} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white">TV Connected!</h2>
              <p className="text-sm text-gray-300 mt-1 font-bold">
                Linked as <span className="text-brand-primary">&ldquo;{approvedDetails?.screenName}&rdquo;</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Launching live menu for {approvedDetails?.venueSlug}...
              </p>
            </div>
          </div>
        ) : activeTab === 'pair' ? (
          /* Smart TV Pairing View (QR + 6-digit Code) */
          <div className="space-y-6">
            <div className="bg-black/40 border border-white/10 rounded-2xl p-6 relative overflow-hidden">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                TV Pairing Code
              </p>

              {/* Big 6-Digit Display */}
              <div className="my-2">
                <code className="text-4xl sm:text-5xl font-mono font-black text-brand-primary tracking-[0.2em] drop-shadow-md">
                  {isGeneratingCode ? 'GENERATING' : formattedCode}
                </code>
              </div>

              {/* Countdown / Status */}
              <div className="flex items-center justify-center gap-2 text-xs font-bold text-gray-500 mt-3">
                <Clock size={14} />
                <span>
                  {timeRemaining === 'Expired'
                    ? 'Code expired'
                    : `Expires in ${timeRemaining || '15:00'}`}
                </span>
                <button
                  type="button"
                  onClick={requestPairingCode}
                  disabled={isGeneratingCode}
                  className="ml-2 text-brand-primary hover:underline flex items-center gap-1 text-xs cursor-pointer font-bold"
                >
                  <RefreshCw size={12} className={isGeneratingCode ? 'animate-spin' : ''} />
                  New Code
                </button>
              </div>

              {pairingError && (
                <p className="text-xs text-red-400 font-bold mt-2">{pairingError}</p>
              )}
            </div>

            {/* QR Code and Instructions Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center bg-white/[0.02] border border-white/5 rounded-2xl p-5">
              {/* QR Code */}
              <div className="flex flex-col items-center justify-center">
                <div className="p-3 bg-white rounded-2xl shadow-xl border border-white/20 inline-block">
                  {qrPairingUrl ? (
                    <QRCodeSVG
                      value={qrPairingUrl}
                      size={140}
                      level="M"
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                    />
                  ) : (
                    <div className="w-[140px] h-[140px] bg-gray-200 animate-pulse rounded-lg" />
                  )}
                </div>
                <span className="text-[11px] font-bold text-gray-400 mt-2 flex items-center gap-1">
                  <Smartphone size={12} className="text-brand-primary" />
                  Scan with Phone Camera
                </span>
              </div>

              {/* 3 Step Instructions */}
              <div className="text-left space-y-3 text-xs">
                <div className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-primary text-black font-black text-[11px]">
                    1
                  </span>
                  <p className="text-gray-300">
                    Open your smartphone camera & scan the QR code (or visit <strong className="text-white">{typeof window !== 'undefined' ? window.location.host : 'signage.tablegreet.com'}/pair</strong>).
                  </p>
                </div>

                <div className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-primary text-black font-black text-[11px]">
                    2
                  </span>
                  <p className="text-gray-300">
                    Give this TV a name (e.g. <strong className="text-white">Bar TV 1</strong>, <strong className="text-white">Patio Display</strong>).
                  </p>
                </div>

                <div className="flex items-start gap-2.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-primary text-black font-black text-[11px]">
                    3
                  </span>
                  <p className="text-gray-300">
                    Tap <strong className="text-white">Connect</strong>. This TV launches automatically!
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Resume Previous Display */}
            {lastSlug && (
              <div className="pt-2 text-left">
                <button
                  type="button"
                  onClick={() => navigate(`/${lastSlug}/signage`)}
                  className="w-full flex items-center justify-between p-3.5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <Tv className="w-4 h-4 text-brand-primary" />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                        Resume Previous Venue Display
                      </p>
                      <p className="font-mono text-sm font-bold text-white">{lastSlug}</p>
                    </div>
                  </div>
                  <span className="text-xs text-brand-primary font-bold">Resume &rarr;</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Manual Entry View (PIN or Direct Slug) */
          <div className="space-y-6 text-left">
            {/* Enter PIN generated from Admin */}
            <form onSubmit={handleManualCodeSubmit} className="space-y-3 p-5 rounded-2xl bg-black/40 border border-white/10">
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-1.5 mb-1">
                  <KeyRound size={14} className="text-brand-primary" />
                  Enter PIN Code from Admin
                </p>
                <p className="text-[11px] text-gray-400 mb-3">
                  If your manager generated a pairing PIN code in Venue Admin, enter it below.
                </p>
                <input
                  type="text"
                  value={manualCodeInput}
                  onChange={(e) => setManualCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. 748-291"
                  maxLength={10}
                  className="w-full h-12 rounded-xl border border-white/15 bg-black/60 px-4 text-center font-mono text-lg font-bold text-white outline-none focus:border-brand-primary"
                />
              </div>

              {manualCodeError && (
                <p className="text-xs text-red-400 font-bold">{manualCodeError}</p>
              )}

              <button
                type="submit"
                disabled={!manualCodeInput.trim() || isVerifyingManualCode}
                className="w-full h-11 rounded-xl bg-brand-primary text-black font-black uppercase tracking-wider text-xs transition-transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer shadow-md hover:brightness-110"
              >
                {isVerifyingManualCode ? 'Verifying...' : 'Connect Display with PIN'}
                <ArrowRight size={14} />
              </button>
            </form>

            {/* Direct Slug Launch */}
            <form onSubmit={handleLaunchSlug} className="space-y-3 p-5 rounded-2xl bg-white/[0.02] border border-white/5">
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-1.5 mb-1">
                  <MonitorPlay size={14} className="text-brand-primary" />
                  Direct Venue Slug
                </p>
                <p className="text-[11px] text-gray-400 mb-3">
                  Launch menu directly using your restaurant slug name.
                </p>
                <input
                  type="text"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  placeholder="e.g. armani-ristorante-dubai"
                  className="w-full h-12 rounded-xl border border-white/15 bg-black/60 px-4 text-sm font-bold text-white outline-none focus:border-brand-primary"
                />
              </div>

              <button
                type="submit"
                disabled={!slugInput.trim()}
                className="w-full h-11 rounded-xl bg-white/10 text-white hover:bg-white/15 font-black uppercase tracking-wider text-xs transition-colors disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
              >
                Launch Live Menu
                <ArrowRight size={14} />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
